import { motion } from "framer-motion";
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Library, Rocket, Sparkles, Book } from "lucide-react";
import StarrySkyBackground from '@/components/StarrySkyBackground';

const StoryLimitPage = ({ plan = 'FREE' }) => {
    const navigate = useNavigate();

    const variants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const content = {
        FREE: {
            title: "Your First Story is Complete! 🎉",
            description: "Ready to unlock unlimited storytelling possibilities?",
            features: [
                "Create unlimited magical stories",
                "Access premium story themes and characters",
                "Save and share your stories with family",
                "Priority story generation"
            ],
            primaryAction: {
                text: "Upgrade to Family Plan",
                onClick: () => navigate('/plans')
            }
        },
        FAMILY: {
            title: "Time for a Story Break! ✨",
            description: "You've reached today's story limit. Ready to unlock unlimited daily stories?",
            features: [
                "Create unlimited stories every day",
                "Access exclusive premium themes",
                "Priority story generation",
                "Advanced customization options"
            ],
            primaryAction: {
                text: "Upgrade to Family Pro",
                onClick: () => navigate('/plans')
            },
            secondaryAction: {
                text: "View My Library",
                onClick: () => navigate('/library')
            }
        },
        FAMILY_PRO: {
            title: "What a Creative Day! 🌟",
            description: "You've reached today's story limit. Come back tomorrow for more magical adventures!",
            message: "Why not revisit some of your amazing stories while you wait?",
            action: {
                text: "Go to My Library",
                onClick: () => navigate('/library')
            }
        }
    };

    const currentContent = content[plan];

    return (
        <div className="grid place-items-center">
            <StarrySkyBackground showAurora={true} showClouds={true} showShootingStar={false} />

            <div className="container relative px-4 py-8 mx-auto">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={{
                        visible: { transition: { staggerChildren: 0.1 } }
                    }}
                    className="max-w-2xl mx-auto text-center"
                >
                    <motion.h1
                        variants={variants}
                        className="mb-6 text-4xl font-bold md:text-5xl"
                    >
                        {currentContent.title}
                    </motion.h1>

                    <motion.p
                        variants={variants}
                        className="mb-8 text-xl text-muted-foreground"
                    >
                        {currentContent.description}
                    </motion.p>

                    {plan !== 'FAMILY_PRO' ? (
                        <motion.div variants={variants}>
                            <Card className="p-6 mb-8 bg-card/50 backdrop-blur">
                                <h2 className="flex items-center justify-center gap-2 mb-6 text-2xl font-semibold">
                                    <Sparkles className="w-6 h-6" />
                                    Unlock Premium Features
                                </h2>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    {currentContent.features.map((feature, index) => (
                                        <motion.div
                                            key={index}
                                            variants={{
                                                hidden: { opacity: 0, x: -20 },
                                                visible: {
                                                    opacity: 1,
                                                    x: 0,
                                                    transition: { delay: index * 0.1 }
                                                }
                                            }}
                                            className="flex items-center gap-2 text-left"
                                        >
                                            <Rocket className="w-5 h-5 text-primary" />
                                            <span>{feature}</span>
                                        </motion.div>
                                    ))}
                                </div>
                            </Card>
                        </motion.div>
                    ) : (
                        <motion.p
                            variants={variants}
                            className="mb-8 text-lg"
                        >
                            {currentContent.message}
                        </motion.p>
                    )}

                    <motion.div
                        variants={variants}
                        className="space-y-4"
                    >
                        {currentContent.primaryAction && (
                            <Button
                                size="lg"
                                onClick={currentContent.primaryAction.onClick}
                                className="px-8 text-lg"
                            >
                                {currentContent.primaryAction.text}
                            </Button>
                        )}

                        {(plan === 'FAMILY' || plan === 'FAMILY_PRO') && (
                            <div className="flex justify-center">
                                <Button
                                    variant="outline"
                                    size="lg"
                                    onClick={plan === 'FAMILY' ?
                                        currentContent.secondaryAction.onClick :
                                        currentContent.action.onClick}
                                    className="px-8 text-lg"
                                >
                                    <Library className="w-5 h-5 mr-2" />
                                    {plan === 'FAMILY' ?
                                        currentContent.secondaryAction.text :
                                        currentContent.action.text}
                                </Button>
                            </div>
                        )}
                    </motion.div>

                    {plan !== 'FAMILY_PRO' && (
                        <motion.div
                            variants={variants}
                            className="flex items-center justify-center mt-12 text-sm text-muted-foreground"
                        >
                            <Book className="w-4 h-4 mr-2" />
                            Upgrade now and start creating more magical stories!
                        </motion.div>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default StoryLimitPage;